import { useForm } from 'react-hook-form/dist/index.ie11';
import { Button, Column, Row } from '@grubhubprod/gh-cookbook-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { Input } from 'src/components/Input/Input';
import { IDepartment, SHORT_SEARCH_DELAY } from 'src/utilities/useSearchHooks';

interface FormFields {
  departmentName: string;
}

export interface EnterpriseDepartmentCreateUpdateProps {
  onSuccess: () => void;
  onCancel: () => void;
  department?: IDepartment;
}

export default function EnterpriseDepartmentForm({
  onCancel,
  onSuccess,
  department,
}: EnterpriseDepartmentCreateUpdateProps): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const { register, handleSubmit, formState, errors } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: { departmentName: department ? department.name : '' },
  });
  const { isValid, isSubmitting } = formState;

  async function addNewDepartment({ departmentName }: FormFields) {
    try {
      await sdk.http.post(`/corp-admin/${corpid}/departments`, {
        owner_id: corpid,
        name: departmentName,
      });
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          onSuccess();
          pushSuccess('Your department was added');
          resolve();
        }, SHORT_SEARCH_DELAY);
      });
    } catch (error) {
      pushError(`Your department was not added. Try again.`);
    }
  }

  async function updateDepartment({ departmentName }: FormFields) {
    try {
      await sdk.http.put(`/corp-admin/${corpid}/departments/${department.id}`, {
        owner_id: corpid,
        name: departmentName,
      });
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          onSuccess();
          pushSuccess('Your department was updated');
          resolve();
        }, SHORT_SEARCH_DELAY);
      });
    } catch (error) {
      pushError(`Your department was not updated. Try again.`);
    }
  }

  return (
    <form style={{ width: '100%' }}>
      <Row gutter={8}>
        <Column gutter={8} phone={6} tablet={6} desktop={4}>
          <Input
            id="departmentName"
            name="departmentName"
            ariaLabel="Department name"
            placeholder="Department name"
            isSmall
            maxLength={50}
            disabled={isSubmitting}
            ref={register({
              maxLength: {
                value: 50,
                message: 'Department name must be 50 characters or less',
              },
              required: true,
            })}
            error={errors.departmentName}
          />
        </Column>
        <Column gutter={8} phone={3} tablet={3} desktop={2}>
          <Button
            variant="primary"
            block
            buttonSize="small"
            disabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleSubmit(
              department ? updateDepartment : addNewDepartment
            )}
          >
            {department ? 'Update' : 'Add'}
          </Button>
        </Column>
        <Column gutter={8} phone={3} tablet={2} desktop={2}>
          <Button
            disabled={isSubmitting}
            buttonSize="small"
            variant="tertiary"
            block
            type="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Column>
      </Row>
    </form>
  );
}
